import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  TextField,
  AppBar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Menu,
  MenuItem,
  InputAdornment,
  Drawer,
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  Note as NoteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Settings as SettingsIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { ownerService } from '../../services/ownerService';
import { getNewHires, updateNewHire, NewHire } from '../../services/hireService';
import { hireCodeRatioSettingsService, HireCodeRatioConfig } from '../../services/hireCodeRatioSettingsService';
import { getTeamMemberHires, getOwnerMemberHires } from '../../services/ownerTeamHireService';
import { progressStageService, ProgressStage } from '../../services/progressStageService';
import { getTeamMemberHireRatio } from '../../services/teamHireCodeService';
import TrademarkFooter from '../common/TrademarkFooter';
import ConfirmArchive from '../archive/ConfirmArchive';
import Archived from '../archive/Archived';
import MassArchiveDialog from '../archive/MassArchiveDialog';
import Analytics from './Analytics';
import { supabase } from '../../config/supabase';
import MoveHireButton from '../hire/MoveHireButton';
import MoveHireDialog from '../hire/MoveHireDialog';
import FrontendSMSManager from '../messaging/FrontendSMSManager';
import { getOwnerArchivedHires } from '../../services/ownerArchiveService';
import TimeSinceCell from '../common/TimeSinceCell';
import EmailReminders from '../email/EmailReminders';
import { alpha } from '@mui/material/styles';
import ChecklistContentSettings from '../ownerconfig/ChecklistContentSettings';
import ChecklistAutomationSettings from '../ownerconfig/ChecklistAutomationSettings';
import ProgressStageSettings from '../ownerconfig/ProgressStageSettings';
import HireCodeRatioSettings from '../ownerconfig/HireCodeRatioSettings';
import ProLeadGenerator from '../leads/ProLeadGenerator';
import NotesDialog from '../hire/NotesDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type TeamMemberMetric = {
  enrolled: number;
  waiting: number;
  ratio: number | null;
  currentEnrolled: number;
  currentWaiting: number;
  currentRatio: number | null;
  jotformEnrolled: number;
  jotformInvitations: number;
  jotformRatio: number | null;
  jotformCurrentEnrolled: number;
  jotformCurrentInvitations: number;
  jotformCurrentRatio: number | null;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`owner-tabpanel-${index}`}
      aria-labelledby={`owner-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const OwnerDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [ownerProfile, setOwnerProfile] = useState<any>(null);
  const [hires, setHires] = useState<NewHire[]>([]);
  const [archivedHires, setArchivedHires] = useState<NewHire[]>([]);
  const [hiresLoading, setHiresLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<NewHire>>({});
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [hireToArchive, setHireToArchive] = useState<NewHire | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [massArchiveOpen, setMassArchiveOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [filteredHires, setFilteredHires] = useState<NewHire[]>([]);
  const [collapsedStages, setCollapsedStages] = useState<Set<number>>(new Set());
  const [assignRecruiterOpen, setAssignRecruiterOpen] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState<any>(null);
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedHireForActions, setSelectedHireForActions] = useState<NewHire | null>(null);
  const [moveHireOpen, setMoveHireOpen] = useState(false);
  const [selectedHireForMove, setSelectedHireForMove] = useState<NewHire | null>(null);
  const [search, setSearch] = useState('');
  const [teamMemberMetrics, setTeamMemberMetrics] = useState<Record<string, TeamMemberMetric>>({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [activeConfigView, setActiveConfigView] = useState<'content' | 'automation' | 'progressStages' | 'hireCodeRatio' | null>(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedHireForNotes, setSelectedHireForNotes] = useState<NewHire | null>(null);
  const [ratioSettings, setRatioSettings] = useState<HireCodeRatioConfig>(hireCodeRatioSettingsService.getDefaultConfig());
  const theme = useTheme();

  // Filter team members to only include admins (exclude owners) for buttons
  const adminTeamMembers = teamMembers.filter(member => member.role === 'admin');
  const ownerTeamMembers = teamMembers.filter(member => member.role === 'owner');

  useEffect(() => {
    fetchDashboardData();
    fetchAllHires();
  }, []);

  // Fetch metrics for all team members when team members are loaded
  useEffect(() => {
    if (teamMembers.length > 0) {
      fetchTeamMemberMetrics();
    }
  }, [teamMembers]);

  // Update filtered hires when hires or selected team member changes
  useEffect(() => {
    if (selectedTeamMember) {
      // Filter hires based on selected team member
      const teamMemberEmail = selectedTeamMember.team_member_email || selectedTeamMember.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        
        // Convert to string and check if it contains the team member email
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
    } else {
      // Show all hires when no team member is selected
      setFilteredHires(hires);
    }
  }, [hires, selectedTeamMember]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch owner profile
      const profile = await ownerService.getCurrentOwnerProfile();
      setOwnerProfile(profile);

      // Fetch organization statistics
      const organizationStats = await ownerService.getOrganizationStats();
      setStats(organizationStats);

      // Fetch organization team members
      const orgTeamMembers = await ownerService.getOrganizationTeamMembers();
      setTeamMembers(orgTeamMembers);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllHires = async () => {
    try {
      setHiresLoading(true);
      console.log('Fetching all hires in OwnerDashboard...');
      
      // Fetch progress stages for current user's organization (DB override if present)
      const stages = await progressStageService.getCurrentUserProgressStagesWithDbOverride();
      setProgressStages(stages);

      // Fetch hire/code ratio settings for current user's organization
      const ratioConfig = await hireCodeRatioSettingsService.getCurrentUserSettings();
      setRatioSettings(ratioConfig);

      const [activeData, archivedData] = await Promise.all([
        getNewHires(),
        getOwnerArchivedHires().catch(error => {
          console.error('Error fetching archived hires in OwnerDashboard:', error);
          return [];
        })
      ]);

      console.log('Received all hires in OwnerDashboard:', activeData);
      setHires(activeData);
      setArchivedHires(archivedData);
    } catch (error) {
      console.error('Error fetching all hires:', error);
    } finally {
      setHiresLoading(false);
    }
  };

  const fetchTeamMemberMetrics = async () => {
    try {
      setMetricsLoading(true);
      const metrics: Record<string, TeamMemberMetric> = {};
      
      // Fetch metrics for each team member
      await Promise.all(
        teamMembers.map(async (member) => {
          const email = member.team_member_email || member.email;
          const defaultMetric: TeamMemberMetric = {
            enrolled: 0,
            waiting: 0,
            ratio: null,
            currentEnrolled: 0,
            currentWaiting: 0,
            currentRatio: null,
            jotformEnrolled: 0,
            jotformInvitations: 0,
            jotformRatio: null,
            jotformCurrentEnrolled: 0,
            jotformCurrentInvitations: 0,
            jotformCurrentRatio: null,
          };
          if (email) {
            try {
              const metric = await getTeamMemberHireRatio(email);
              metrics[member.id] = metric;
            } catch (error) {
              console.error(`Error fetching metrics for ${email}:`, error);
              metrics[member.id] = defaultMetric;
            }
          } else {
            metrics[member.id] = defaultMetric;
          }
        })
      );
      
      setTeamMemberMetrics(metrics);
    } catch (error) {
      console.error('Error fetching team member metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
    // Clear config view when switching tabs
    // If clicking Overview (tab 0) while a config view is active, close the config view
    if (newValue === 0 && activeConfigView !== null) {
      setActiveConfigView(null);
    } else if (newValue !== 0) {
      setActiveConfigView(null);
    }
  };

  const navigationItems = [
    { id: 0, label: 'Overview', icon: <DashboardIcon /> },
    { id: 1, label: 'Team Members', icon: <PeopleIcon /> },
    { id: 2, label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 3, label: 'SMS Manager', icon: <MessageIcon /> },
    { id: 4, label: 'Email Reminders', icon: <EmailIcon /> },
  ];

  const glonboardingItems = [
    { id: 5, label: 'Pro Leads', icon: <WorkIcon /> },
  ];

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'new_hire':
        return 'secondary';
      case 'owner':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // CRM Dashboard functions
  const handleEdit = (hire: NewHire) => {
    setEditingId(hire.id);
    setEditFormData(hire);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSave = async () => {
    if (editingId) {
      try {
        await updateNewHire(editingId, editFormData);
        await fetchAllHires(); // Refresh the list after updating
        setEditingId(null);
        setEditFormData({});
      } catch (error) {
        console.error('Error updating hire:', error);
      }
    }
  };

  const handleEditChange = (field: keyof NewHire) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditFormData({
      ...editFormData,
      [field]: event.target.value,
    });
  };

  const handleArchive = async (hire: NewHire) => {
    try {
      // Insert into new_hire_archive table with current timestamp
      const { error: insertError } = await supabase
        .from('new_hire_archive')
        .insert([{
          ...hire,
          created_at: new Date().toISOString() // Set archive timestamp
        }]);

      if (insertError) throw insertError;

      // Remove from new_hire table
      const { error: deleteError } = await supabase
        .from('new_hire')
        .delete()
        .eq('id', hire.id);

      if (deleteError) throw deleteError;

      // Remove from profiles table (new_hire.id = profiles.id)
      const { error: profileDeleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', hire.id);

      if (profileDeleteError) {
        console.warn('Error deleting profile (may not exist):', profileDeleteError);
        // Don't throw error - profile might not exist, continue with archive process
      }

      // Refresh the list after archiving
      await fetchAllHires();
      console.log('Successfully archived hire:', hire.fullName);
      
      // Close the confirmation dialog
      setConfirmArchiveOpen(false);
      setHireToArchive(null);
    } catch (error) {
      console.error('Error archiving hire:', error);
    }
  };

  const handleArchiveClick = (hire: NewHire) => {
    setHireToArchive(hire);
    setConfirmArchiveOpen(true);
  };

  const handleArchiveCancel = () => {
    setConfirmArchiveOpen(false);
    setHireToArchive(null);
  };

  const handleArchiveConfirm = () => {
    if (hireToArchive) {
      handleArchive(hireToArchive);
    }
  };


  const handleTeamMemberClick = async (member: any) => {
    try {
      setHiresLoading(true);
      console.log('Team member clicked:', member.full_name);
      
      if (selectedTeamMember?.id === member.id) {
        // If clicking the same team member, deselect them
        setSelectedTeamMember(null);
        setFilteredHires(hires);
      } else {
        // Select the new team member and fetch their hires
        setSelectedTeamMember(member);
        
        // Use the team member service to get filtered hires
        const teamMemberEmail = member.team_member_email || member.email;
        const teamMemberHires = await getTeamMemberHires(teamMemberEmail);
        setFilteredHires(teamMemberHires);
      }
    } catch (error) {
      console.error('Error fetching team member hires:', error);
      // Fallback to client-side filtering
      const teamMemberEmail = member.team_member_email || member.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        
        // Convert to string and check if it contains the team member email
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
      setSelectedTeamMember(member);
    } finally {
      setHiresLoading(false);
    }
  };

  const handleOwnerMemberClick = async (member: any) => {
    try {
      setHiresLoading(true);
      console.log('Owner member clicked:', member.full_name);
      
      if (selectedTeamMember?.id === member.id) {
        // If clicking the same owner member, deselect them
        setSelectedTeamMember(null);
        setFilteredHires(hires);
      } else {
        // Select the new owner member and fetch their hires
        setSelectedTeamMember(member);
        
        // Use the owner member service to get filtered hires
        const ownerMemberEmail = member.team_member_email || member.email;
        const ownerMemberHires = await getOwnerMemberHires(ownerMemberEmail);
        setFilteredHires(ownerMemberHires);
      }
    } catch (error) {
      console.error('Error fetching owner member hires:', error);
      // Fallback to client-side filtering
      const ownerMemberEmail = member.team_member_email || member.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        
        // Convert to string and check if it contains the owner member email
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(ownerMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
      setSelectedTeamMember(member);
    } finally {
      setHiresLoading(false);
    }
  };

  const handleShowAllHires = () => {
    setSelectedTeamMember(null);
    setFilteredHires(hires);
  };

  const renderEditableCell = (hire: NewHire, field: keyof NewHire) => {
    const isEditing = editingId === hire.id;
    const value = isEditing ? editFormData[field] || '' : hire[field];

    if (isEditing && field !== 'created_at' && field !== 'update_timestamp') {
      return (
        <TextField
          fullWidth
          size="small"
          value={value}
          onChange={handleEditChange(field)}
          type="text"
        />
      );
    }

    // Format the timestamp for created_at and update_timestamp fields
    if ((field === 'created_at' || field === 'update_timestamp') && value) {
      const date = new Date(value as string);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }

    return value;
  };

  const tableColumns = [
    'Actions',
    'Notes',
    'Created At',
    'Updated At',
    'Time Since',
    'Full Name',
    'State',
    'Email',
    'Mobile Number',
    'Instagram',
    'Reference',
    'Recruiter Numbers',
    'Recruiter Emails',
    'Edit',
  ];

  const [progressStages, setProgressStages] = useState<ProgressStage[]>([]);

  const agentNumberCheckboxStage = useMemo(() => {
    if (!progressStages || progressStages.length === 0) return null;

    const pendingStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('pending waiting for training')
    );
    if (pendingStage) return pendingStage.value;

    const waitingApprovalStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('waiting agent number approval')
    );
    if (waitingApprovalStage) return waitingApprovalStage.value;

    const genericAgentStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('agent number')
    );
    return genericAgentStage?.value ?? null;
  }, [progressStages]);

  const getTableColumnsForStage = (stageValue: number, agentStageValue: number | null) => {
    if (agentStageValue !== null && stageValue === agentStageValue) {
      const baseColumns = [...tableColumns];
      // Insert 'Agent Number Sent' after 'Edit' (index 2)
      baseColumns.splice(2, 0, 'Agent Number Sent');
      return baseColumns;
    }
    return tableColumns;
  };

  const toggleStageCollapse = (stageIndex: number) => {
    setCollapsedStages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stageIndex)) {
        newSet.delete(stageIndex);
      } else {
        newSet.add(stageIndex);
      }
      return newSet;
    });
  };


  const handleAssignRecruiter = (hire: NewHire) => {
    setSelectedRecruiter(hire);
    
    // Handle existing recruiter emails - convert to array if it's a string or single email
    let existingEmails: string[] = [];
    
    if (hire.recruiter_emails) {
      if (Array.isArray(hire.recruiter_emails)) {
        existingEmails = hire.recruiter_emails;
      } else if (typeof hire.recruiter_emails === 'string') {
        // Handle case where it might be a JSON string or comma-separated string
        try {
          const parsed = JSON.parse(hire.recruiter_emails);
          existingEmails = Array.isArray(parsed) ? parsed : [hire.recruiter_emails];
        } catch {
          // If not JSON, treat as comma-separated or single email
          const recruiterEmailsStr = hire.recruiter_emails as string;
          existingEmails = recruiterEmailsStr.includes(',') 
            ? recruiterEmailsStr.split(',').map((email: string) => email.trim())
            : [recruiterEmailsStr];
        }
      } else {
        existingEmails = [String(hire.recruiter_emails)];
      }
    }
    
    console.log('Existing recruiter emails:', existingEmails);
    console.log('Admin team members:', adminTeamMembers.map(m => m.team_member_email || m.email));
    console.log('Owner team members:', ownerTeamMembers.map(m => m.team_member_email || m.email));
    
    // Separate existing emails into admins and owners based on team members
    const existingAdmins = existingEmails.filter(email => 
      adminTeamMembers.some(admin => {
        const adminEmail = admin.team_member_email || admin.email;
        return adminEmail && adminEmail.toLowerCase() === email.toLowerCase();
      })
    );
    
    const existingOwners = existingEmails.filter(email => 
      ownerTeamMembers.some(owner => {
        const ownerEmail = owner.team_member_email || owner.email;
        return ownerEmail && ownerEmail.toLowerCase() === email.toLowerCase();
      })
    );
    
    console.log('Found existing admins:', existingAdmins);
    console.log('Found existing owners:', existingOwners);
    
    setSelectedAdmins(existingAdmins);
    setSelectedOwners(existingOwners);
    setAssignRecruiterOpen(true);
  };

  const handleAssignRecruiterConfirm = async () => {
    if (selectedRecruiter) {
      try {
        // Combine selected admins and owners into recruiter_emails
        const allSelectedEmails = [...selectedAdmins, ...selectedOwners];
        
        console.log('Assigning recruiters:', allSelectedEmails);
        
        // Update the recruiter_emails field with selected admin and owner emails
        const updatedHire = {
          ...selectedRecruiter,
          recruiter_emails: allSelectedEmails
        };
        
        await updateNewHire(selectedRecruiter.id, updatedHire);
        await fetchAllHires(); // Refresh the list after updating
        setAssignRecruiterOpen(false);
        setSelectedRecruiter(null);
        setSelectedAdmins([]);
        setSelectedOwners([]);
      } catch (error) {
        console.error('Error assigning recruiter:', error);
      }
    }
  };

  const handleAssignRecruiterCancel = () => {
    setAssignRecruiterOpen(false);
    setSelectedRecruiter(null);
    setSelectedAdmins([]);
    setSelectedOwners([]);
  };

  const handleAdminToggle = (adminEmail: string) => {
    setSelectedAdmins(prev => {
      if (prev.includes(adminEmail)) {
        return prev.filter(email => email !== adminEmail);
      } else {
        return [...prev, adminEmail];
      }
    });
  };

  const handleOwnerToggle = (ownerEmail: string) => {
    setSelectedOwners(prev => {
      if (prev.includes(ownerEmail)) {
        return prev.filter(email => email !== ownerEmail);
      } else {
        return [...prev, ownerEmail];
      }
    });
  };

  // Handle agent appointment sent checkbox change
  const handleAgentAppointmentSentChange = async (hire: NewHire, checked: boolean) => {
    try {
      await updateNewHire(hire.id, { agent_appointment_sent: checked });
      await fetchAllHires(); // Refresh the list after updating
    } catch (error) {
      console.error('Error updating agent appointment sent status:', error);
    }
  };

  // NEW: Handle move success
  const handleMoveSuccess = (updatedHire: NewHire) => {
    // Update the local state with the moved hire
    setHires(hires.map(hire => hire.id === updatedHire.id ? updatedHire : hire));
    console.log('Hire moved successfully:', updatedHire.fullName);
  };

  // NEW: Actions menu handlers
  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>, hire: NewHire) => {
    setActionsMenuAnchor(event.currentTarget);
    setSelectedHireForActions(hire);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
    setSelectedHireForActions(null);
  };

  const handleAssignFromMenu = () => {
    if (selectedHireForActions) {
      handleAssignRecruiter(selectedHireForActions);
    }
    handleActionsMenuClose();
  };

  const handleArchiveFromMenu = () => {
    if (selectedHireForActions) {
      handleArchiveClick(selectedHireForActions);
    }
    handleActionsMenuClose();
  };

  const handleMoveFromMenu = () => {
    if (selectedHireForActions) {
      setSelectedHireForMove(selectedHireForActions);
      setMoveHireOpen(true);
    }
    handleActionsMenuClose();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    setSearch(searchTerm);
    
    // Filter hires based on search term across all relevant fields
    const filtered = hires.filter(hire => {
      if (searchTerm === '') return true;
      
      // Search across multiple fields
      const searchableFields = [
        hire.fullName,
        hire.email,
        hire.mobileNumber,
        hire.state,
        hire.instagram,
        hire.reference,
        hire.recruiter_numbers,
        hire.recruiter_emails,
      ].map(field => String(field || '').toLowerCase());
      
      return searchableFields.some(field => field.includes(searchTerm));
    });
    
    // Apply team member filter if one is selected
    if (selectedTeamMember) {
      const teamMemberEmail = selectedTeamMember.team_member_email || selectedTeamMember.email;
      const teamFiltered = filtered.filter(hire => {
        if (!hire.recruiter_emails) return false;
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(teamFiltered);
    } else {
      setFilteredHires(filtered);
    }
  };

  const combinedHires = useMemo<NewHire[]>(() => {
    const merged = new Map<string, NewHire>();
    (archivedHires || []).forEach(hire => {
      if (hire?.id) merged.set(hire.id, hire);
    });
    (hires || []).forEach(hire => {
      if (hire?.id) merged.set(hire.id, hire);
    });
    return Array.from(merged.values());
  }, [hires, archivedHires]);

  const { jotformEnrolled, jotformInvitations, jotformRatio } = useMemo(() => {
    const enrolledCount = combinedHires.filter(hire => ratioSettings.jotform_enrolled.includes(Number(hire.progress))).length;
    const invitationCount = combinedHires.filter(hire => ratioSettings.jotform_invitation.includes(Number(hire.progress))).length;
    const ratio = invitationCount === 0 ? null : enrolledCount / invitationCount;
    return { jotformEnrolled: enrolledCount, jotformInvitations: invitationCount, jotformRatio: ratio };
  }, [combinedHires, ratioSettings]);

  const { jotformCurrentEnrolled, jotformCurrentInvitations, jotformCurrentRatio } = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 2);
    const filtered = combinedHires.filter(hire => {
      if (!hire?.created_at) return false;
      const createdDate = new Date(hire.created_at as string);
      if (Number.isNaN(createdDate.getTime())) return false;
      return createdDate >= cutoffDate;
    });

    const enrolledCount = filtered.filter(hire => ratioSettings.jotform_enrolled.includes(Number(hire.progress))).length;
    const invitationCount = filtered.filter(hire => ratioSettings.jotform_invitation.includes(Number(hire.progress))).length;
    const ratio = invitationCount === 0 ? null : enrolledCount / invitationCount;

    return {
      jotformCurrentEnrolled: enrolledCount,
      jotformCurrentInvitations: invitationCount,
      jotformCurrentRatio: ratio,
    };
  }, [combinedHires, ratioSettings]);

  const {
    hirePerCodeRecentEnrolled,
    hirePerCodeRecentWaiting,
    hirePerCodeRecentRatio,
    hirePerCodeAllTimeEnrolled,
    hirePerCodeAllTimeWaiting,
    hirePerCodeAllTimeRatio,
  } = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 2);

    const recentHires = combinedHires.filter(hire => {
      if (!hire?.created_at) return false;
      const createdAt = new Date(hire.created_at as string);
      if (Number.isNaN(createdAt.getTime())) return false;
      return createdAt >= cutoff;
    });

    const toProgress = (hire: NewHire) => Number(hire?.progress ?? 0);

    const recentEnrolled = recentHires.filter(hire => ratioSettings.hire_stages.includes(toProgress(hire))).length;
    const recentWaiting = recentHires.filter(hire => ratioSettings.coded_stages.includes(toProgress(hire))).length;
    const recentRatio = recentEnrolled === 0 ? null : recentWaiting / recentEnrolled;

    const allTimeEnrolled = combinedHires.filter(hire => ratioSettings.hire_stages.includes(toProgress(hire))).length;
    const allTimeWaiting = combinedHires.filter(hire => ratioSettings.coded_stages.includes(toProgress(hire))).length;
    const allTimeRatio = allTimeEnrolled === 0 ? null : allTimeWaiting / allTimeEnrolled;

    return {
      hirePerCodeRecentEnrolled: recentEnrolled,
      hirePerCodeRecentWaiting: recentWaiting,
      hirePerCodeRecentRatio: recentRatio,
      hirePerCodeAllTimeEnrolled: allTimeEnrolled,
      hirePerCodeAllTimeWaiting: allTimeWaiting,
      hirePerCodeAllTimeRatio: allTimeRatio,
    };
  }, [combinedHires, ratioSettings]);

  const formatNumber = useCallback((value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '0';
    }
    return Number(value).toLocaleString('en-US');
  }, []);

  const formatPercent = useCallback((ratio: number | null) => {
    if (ratio === null) return 'N/A';
    const clamped = Math.min(ratio * 100, 100);
    return `${clamped.toFixed(0)}%`;
  }, []);

  const metricItems = useMemo(() => [
    {
      key: 'active-hires',
      label: 'Active Hires',
      value: formatNumber(stats?.activeHires ?? 0),
      helperText: 'Hires currently progressing',
      accentColor: '#d97706',
    },
    {
      key: 'jotform-all-time',
      label: 'Jotform to Hire Ratio (All Time)',
      value: formatPercent(jotformRatio),
      helperText: 'Active + archived invites',
      accentColor: '#1c7ed6',
      tooltip: `Enrolled (Stages ${ratioSettings.jotform_enrolled.join(',')}): ${formatNumber(jotformEnrolled)} • Invitations (Stages ${ratioSettings.jotform_invitation.join(',')}): ${formatNumber(jotformInvitations)}`,
    },
    {
      key: 'jotform-recent',
      label: 'Jotform to Hire Ratio (Last 2 Months)',
      value: formatPercent(jotformCurrentRatio),
      helperText: 'Invites vs. enrollments in last 60 days',
      accentColor: '#5c7cfa',
      tooltip: `Enrolled (Stages ${ratioSettings.jotform_enrolled.join(',')}): ${formatNumber(jotformCurrentEnrolled)} • Invitations (Stages ${ratioSettings.jotform_invitation.join(',')}): ${formatNumber(jotformCurrentInvitations)}`,
    },
    {
      key: 'hire-per-code-recent',
      label: 'Hire per code ratio (Last 2 Months)',
      value: formatPercent(hirePerCodeRecentRatio),
      helperText: 'Active hires coded in last 60 days',
      accentColor: '#0ca678',
      tooltip: `Hires (Stages ${ratioSettings.hire_stages.join(',')}): ${formatNumber(hirePerCodeRecentEnrolled)} • Coded (Stages ${ratioSettings.coded_stages.join(',')}): ${formatNumber(hirePerCodeRecentWaiting)}`,
    },
    {
      key: 'hire-per-code-all-time',
      label: 'All time hire per code ratio',
      value: formatPercent(hirePerCodeAllTimeRatio),
      helperText: 'Active + archived hires',
      accentColor: '#7048e8',
      tooltip: `Hires (Stages ${ratioSettings.hire_stages.join(',')}): ${formatNumber(hirePerCodeAllTimeEnrolled)} • Coded (Stages ${ratioSettings.coded_stages.join(',')}): ${formatNumber(hirePerCodeAllTimeWaiting)}`,
    },
  ], [
    stats,
    jotformRatio,
    jotformEnrolled,
    jotformInvitations,
    jotformCurrentRatio,
    jotformCurrentEnrolled,
    jotformCurrentInvitations,
    hirePerCodeRecentRatio,
    hirePerCodeRecentEnrolled,
    hirePerCodeRecentWaiting,
    hirePerCodeAllTimeRatio,
    hirePerCodeAllTimeEnrolled,
    hirePerCodeAllTimeWaiting,
    ratioSettings,
    formatNumber,
    formatPercent,
  ]);

  const handleClearSearch = () => {
    setSearch('');
    if (selectedTeamMember) {
      // Reapply team member filter without search
      const teamMemberEmail = selectedTeamMember.team_member_email || selectedTeamMember.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        const recruiterEmailsStr = String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
    } else {
      setFilteredHires(hires);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  const sidebarWidth = 280;
  const sidebarMinimizedWidth = 80;
  const appBarHeight = 64; // Standard Material-UI AppBar height
  const currentSidebarWidth = sidebarMinimized ? sidebarMinimizedWidth : sidebarWidth;

  const toggleSidebar = () => {
    setSidebarMinimized(!sidebarMinimized);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: currentSidebarWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: currentSidebarWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'}`,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e1e1e 0%, #2a2a2a 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            top: appBarHeight,
            height: `calc(100vh - ${appBarHeight}px)`,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {/* Sidebar Header */}
        <Box
          sx={{
            p: sidebarMinimized ? 2 : 3,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e1e1e 0%, #2a2a2a 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
            display: 'flex',
            flexDirection: sidebarMinimized ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: sidebarMinimized ? 'center' : 'space-between',
            gap: 1,
          }}
        >
          {!sidebarMinimized && (
            <>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                  Organization Dashboard
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {ownerProfile?.full_name || 'Owner'}
                </Typography>
              </Box>
            </>
          )}
          <IconButton
            onClick={toggleSidebar}
            sx={{
              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.12)',
              },
              minWidth: sidebarMinimized ? 'auto' : 'auto',
            }}
            size="small"
          >
            {sidebarMinimized ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* Navigation Items */}
        <List sx={{ pt: 2 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
              <Tooltip title={sidebarMinimized ? item.label : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleTabChange(item.id)}
                  selected={selectedTab === item.id}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                    px: sidebarMinimized ? 1 : 2,
                    '&.Mui-selected': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                      },
                    },
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(102, 126, 234, 0.08)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarMinimized ? 0 : 40,
                      justifyContent: 'center',
                      color: selectedTab === item.id
                        ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarMinimized && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: selectedTab === item.id ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        {/* GLONBOARDING PRO Section — restricted to org_id 1 */}
        {ownerProfile?.org_id === 1 && (
        <>
        <Divider sx={{ my: 2, mx: sidebarMinimized ? 1 : 2 }} />
        {!sidebarMinimized && (
          <Box sx={{ px: 2, mb: 1 }}>
            <Typography
              variant="overline"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.15em',
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(90deg, rgba(102,126,234,0.7), rgba(118,75,162,0.7))'
                  : 'linear-gradient(90deg, #667eea, #764ba2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              GLONBOARDING PRO
            </Typography>
          </Box>
        )}
        <List sx={{ pt: 0 }}>
          {glonboardingItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
              <Tooltip title={sidebarMinimized ? item.label : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleTabChange(item.id)}
                  selected={selectedTab === item.id}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                    px: sidebarMinimized ? 1 : 2,
                    '&.Mui-selected': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                      },
                    },
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(102, 126, 234, 0.08)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarMinimized ? 0 : 40,
                      justifyContent: 'center',
                      color: selectedTab === item.id
                        ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarMinimized && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: selectedTab === item.id ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
        </>
        )}

        {/* Separator */}
        <Divider sx={{ my: 2, mx: sidebarMinimized ? 1 : 2 }} />

        {/* Configuration Section */}
        <List sx={{ pt: 0 }}>
          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Checklist Content Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setActiveConfigView(activeConfigView === 'content' ? null : 'content');
                  setSelectedTab(0); // Switch to Overview tab
                }}
                selected={activeConfigView === 'content'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                    },
                  },
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(102, 126, 234, 0.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: activeConfigView === 'content'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Checklist Content Settings"
                    primaryTypographyProps={{
                      fontWeight: activeConfigView === 'content' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>



          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Progress Stage Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setActiveConfigView(activeConfigView === 'progressStages' ? null : 'progressStages');
                  setSelectedTab(0); // Switch to Overview tab
                }}
                selected={activeConfigView === 'progressStages'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                    },
                  },
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(102, 126, 234, 0.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: activeConfigView === 'progressStages'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Progress Stage Settings"
                    primaryTypographyProps={{
                      fontWeight: activeConfigView === 'progressStages' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Hire / Code Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setActiveConfigView(activeConfigView === 'hireCodeRatio' ? null : 'hireCodeRatio');
                  setSelectedTab(0);
                }}
                selected={activeConfigView === 'hireCodeRatio'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                    },
                  },
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(102, 126, 234, 0.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: activeConfigView === 'hireCodeRatio'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Hire / Code Settings"
                    primaryTypographyProps={{
                      fontWeight: activeConfigView === 'hireCodeRatio' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Automation Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setActiveConfigView(activeConfigView === 'automation' ? null : 'automation');
                  setSelectedTab(0); // Switch to Overview tab
                }}
                selected={activeConfigView === 'automation'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                    },
                  },
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(102, 126, 234, 0.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: activeConfigView === 'automation'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Automation Settings"
                    primaryTypographyProps={{
                      fontWeight: activeConfigView === 'automation' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${currentSidebarWidth}px)` },
          mt: `${appBarHeight}px`,
          minHeight: `calc(100vh - ${appBarHeight}px)`,
          transition: 'width 0.3s ease',
        }}
      >
        <Container maxWidth="xl" sx={{ mt: 0, mb: 4, px: 0 }}>
          {/* Statistics Summary */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(180px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                md: 'repeat(auto-fill, minmax(220px, 1fr))',
              },
              gap: { xs: 1.5, md: 2 },
              mb: 3,
              alignItems: 'stretch',
              width: '100%',
              px: 3, // Match TabPanel padding (p: 3)
            }}
          >
        {metricItems.map(item => {
          const accent = item.accentColor;
          const baseBg = alpha(accent, theme.palette.mode === 'dark' ? 0.12 : 0.06);
          const borderColor = alpha(accent, theme.palette.mode === 'dark' ? 0.32 : 0.2);
          const hoverBorderColor = alpha(accent, theme.palette.mode === 'dark' ? 0.45 : 0.32);
          const hoverBg = alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.1);
          const titleColor = theme.palette.mode === 'dark'
            ? alpha('#ffffff', 0.82)
            : alpha(theme.palette.text.primary, 0.88);
          const helperColor = theme.palette.mode === 'dark'
            ? alpha('#ffffff', 0.6)
            : alpha(theme.palette.text.secondary, 0.82);
          const valueColor = theme.palette.mode === 'dark'
            ? alpha('#ffffff', 0.95)
            : alpha(theme.palette.text.primary, 0.95);

          return (
            <Tooltip
              key={item.key}
              title={item.tooltip || ''}
              arrow
              disableHoverListener={!item.tooltip}
              enterTouchDelay={0}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${borderColor}`,
                  backgroundColor: baseBg,
                  px: 2.25,
                  py: 2,
                  minHeight: 92,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: hoverBorderColor,
                    transform: 'translateY(-2px)',
                    backgroundColor: hoverBg,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 20 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: accent,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      color: titleColor,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: valueColor,
                    lineHeight: 1.1,
                    mt: 1,
                  }}
                >
                  {item.value}
                </Typography>
                {item.helperText && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: helperColor,
                      mt: 0.75,
                    }}
                  >
                    {item.helperText}
                  </Typography>
                )}
              </Box>
            </Tooltip>
            );
          })}
          </Box>

          {/* Tab Content */}
          <Box>
            <TabPanel value={selectedTab} index={0}>
              {/* Show config views instead of regular content when active */}
              {activeConfigView === 'content' ? (
                <ChecklistContentSettings onClose={() => setActiveConfigView(null)} />
              ) : activeConfigView === 'automation' ? (
                <ChecklistAutomationSettings onClose={() => setActiveConfigView(null)} />
              ) : activeConfigView === 'progressStages' ? (
                <ProgressStageSettings onClose={() => setActiveConfigView(null)} />
              ) : activeConfigView === 'hireCodeRatio' ? (
                <HireCodeRatioSettings onClose={() => setActiveConfigView(null)} />
              ) : (
                <>
                  {hiresLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <CircularProgress size={40} />
                    </Box>
                  ) : showArchived ? (
                    <Archived onBack={() => setShowArchived(false)} isOwner={true} onUnarchiveComplete={fetchAllHires} />
                  ) : (
                    <>
                      <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 3,
                p: { xs: 1.5, sm: 2 },
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                borderRadius: 2,
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
              }}>
                <Typography 
                  variant="h5" 
                  sx={{
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #ffffff 0%, #b3b3b3 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  All Active Hires Dashboard
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, sm: 2 },
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}>
                  <Button
                    variant="contained"
                    startIcon={<HistoryIcon />}
                    onClick={() => setShowArchived(true)}
                    sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '12px',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      minWidth: { xs: 'auto', sm: 'auto' },
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(102, 126, 234, 0.4)'
                        : '0 4px 20px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #764ba2 0%, #667eea 50%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #764ba2 0%, #667eea 50%, #764ba2 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 25px rgba(102, 126, 234, 0.5)'
                          : '0 8px 25px rgba(102, 126, 234, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      View Archived
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Archived
                    </Box>
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ArchiveIcon />}
                    onClick={() => setMassArchiveOpen(true)}
                    sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '12px',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      minWidth: { xs: 'auto', sm: 'auto' },
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(102, 126, 234, 0.4)'
                        : '0 4px 20px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #764ba2 0%, #667eea 50%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #764ba2 0%, #667eea 50%, #764ba2 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 25px rgba(102, 126, 234, 0.5)'
                          : '0 8px 25px rgba(102, 126, 234, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Mass Archive
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Archive
                    </Box>
                  </Button>
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={fetchAllHires} 
                      color="primary"
                      disabled={hiresLoading}
                      sx={{ 
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #3a3a3a 0%, #4a4a4a 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#ffffff',
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #4a4a4a 0%, #5a5a5a 100%)'
                            : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              {/* Team Member Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                mb: 3,
                p: 2,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.06) 100%)'
                  : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                borderRadius: 2,
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(102, 126, 234, 0.1)'}`,
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    width: '100%', 
                    mb: 2,
                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                >
                  Filters:
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleShowAllHires}
                  sx={{
                    background: selectedTeamMember 
                      ? (theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                      : 'transparent',
                    color: selectedTeamMember 
                      ? '#ffffff'
                      : (theme.palette.mode === 'dark' ? '#667eea' : '#667eea'),
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    border: selectedTeamMember 
                      ? 'none'
                      : `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`,
                    boxShadow: selectedTeamMember
                      ? (theme.palette.mode === 'dark'
                          ? '0 2px 10px rgba(102, 126, 234, 0.3)'
                          : '0 2px 10px rgba(102, 126, 234, 0.2)')
                      : 'none',
                    '&:hover': {
                      background: selectedTeamMember
                        ? (theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                            : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)')
                        : (theme.palette.mode === 'dark'
                            ? 'rgba(102, 126, 234, 0.1)'
                            : 'rgba(102, 126, 234, 0.1)'),
                      transform: 'translateY(-1px)',
                      boxShadow: selectedTeamMember
                        ? (theme.palette.mode === 'dark'
                            ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                            : '0 4px 15px rgba(102, 126, 234, 0.3)')
                        : 'none',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Show All Hires
                </Button>
                
                {/* Owner Team Members */}
                {ownerTeamMembers.length > 0 && (
                  <>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        width: '100%', 
                        mt: 2, 
                        mb: 1,
                        color: theme.palette.mode === 'dark' ? '#b3b3b3' : theme.palette.text.secondary,
                        fontWeight: 500,
                        fontSize: '0.9rem',
                      }}
                    >
                      Org Owner(s):
                    </Typography>
                    {ownerTeamMembers.map((member) => (
                      <Button
                        key={member.id}
                        variant="contained"
                        onClick={() => handleOwnerMemberClick(member)}
                        sx={{
                          background: selectedTeamMember?.id === member.id
                            ? 'transparent'
                            : (theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                          color: selectedTeamMember?.id === member.id
                            ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                            : '#ffffff',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          border: selectedTeamMember?.id === member.id
                            ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                            : 'none',
                          boxShadow: selectedTeamMember?.id === member.id
                            ? 'none'
                            : (theme.palette.mode === 'dark'
                                ? '0 2px 10px rgba(102, 126, 234, 0.3)'
                                : '0 2px 10px rgba(102, 126, 234, 0.2)'),
                          '&:hover': {
                            background: selectedTeamMember?.id === member.id
                              ? (theme.palette.mode === 'dark'
                                  ? 'rgba(102, 126, 234, 0.1)'
                                  : 'rgba(102, 126, 234, 0.1)')
                              : (theme.palette.mode === 'dark'
                                  ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                                  : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'),
                            transform: 'translateY(-1px)',
                            boxShadow: selectedTeamMember?.id === member.id
                              ? 'none'
                              : (theme.palette.mode === 'dark'
                                  ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                                  : '0 4px 15px rgba(102, 126, 234, 0.3)'),
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {member.full_name}
                      </Button>
                    ))}
                  </>
                )}
                
                {/* Admin Team Members */}
                {adminTeamMembers.length > 0 && (
                  <>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        width: '100%', 
                        mt: 2, 
                        mb: 1,
                        color: theme.palette.mode === 'dark' ? '#b3b3b3' : theme.palette.text.secondary,
                        fontWeight: 500,
                        fontSize: '0.9rem',
                      }}
                    >
                      Admins:
                    </Typography>
                    {adminTeamMembers.map((member) => (
                      <Button
                        key={member.id}
                        variant="contained"
                        onClick={() => handleTeamMemberClick(member)}
                        sx={{
                          background: selectedTeamMember?.id === member.id
                            ? 'transparent'
                            : (theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                          color: selectedTeamMember?.id === member.id
                            ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                            : '#ffffff',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          border: selectedTeamMember?.id === member.id
                            ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                            : 'none',
                          boxShadow: selectedTeamMember?.id === member.id
                            ? 'none'
                            : (theme.palette.mode === 'dark'
                                ? '0 2px 10px rgba(102, 126, 234, 0.3)'
                                : '0 2px 10px rgba(102, 126, 234, 0.2)'),
                          '&:hover': {
                            background: selectedTeamMember?.id === member.id
                              ? (theme.palette.mode === 'dark'
                                  ? 'rgba(102, 126, 234, 0.1)'
                                  : 'rgba(102, 126, 234, 0.1)')
                              : (theme.palette.mode === 'dark'
                                  ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                                  : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'),
                            transform: 'translateY(-1px)',
                            boxShadow: selectedTeamMember?.id === member.id
                              ? 'none'
                              : (theme.palette.mode === 'dark'
                                  ? '0 4px 15px rgba(102, 126, 234, 0.4)'
                                  : '0 4px 15px rgba(102, 126, 234, 0.3)'),
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {member.full_name}
                      </Button>
                    ))}
                  </>
                )}
              </Box>
              
              {progressStages.map((stage, stageIndex) => {
                const stageHires = filteredHires.filter(hire => hire.progress === stage.value);
                // Get columns for this specific stage
                let stageTableColumns = getTableColumnsForStage(stage.value, agentNumberCheckboxStage);
                // For "Invitation Sent" stage (stage.value === 0), exclude "Updated At" column
                if (stage.value === 0) {
                  stageTableColumns = stageTableColumns.filter(col => col !== 'Updated At');
                }
                
                return (
                  <Box key={stageIndex} sx={{ mb: 4 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      p: 2,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.3)'}`,
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.16) 100%)'
                          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                        transition: 'all 0.3s ease',
                      },
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => toggleStageCollapse(stageIndex)}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                        }}
                      >
                        {stage.label} ({stageHires.length})
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.1)' 
                              : 'rgba(102, 126, 234, 0.1)',
                          },
                        }}
                      >
                        {collapsedStages.has(stageIndex) ? (
                          <KeyboardArrowDownIcon />
                        ) : (
                          <KeyboardArrowUpIcon />
                        )}
                      </IconButton>
                    </Box>
                    
                    {!collapsedStages.has(stageIndex) && (
                      <>
                        {/* Search Box */}
                        <Box sx={{ 
                          mb: 2, 
                          p: 2,
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.06) 100%)'
                            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(102, 126, 234, 0.1)'}`,
                        }}>
                          <TextField
                            fullWidth
                            placeholder={`Search ${stage.label} hires...`}
                            value={search}
                            onChange={handleSearch}
                            variant="outlined"
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ 
                                    color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                    fontSize: 20 
                                  }} />
                                </InputAdornment>
                              ),
                              endAdornment: search && (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={handleClearSearch}
                                    size="small"
                                    sx={{
                                      color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                      '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' 
                                          ? 'rgba(102, 126, 234, 0.1)' 
                                          : 'rgba(102, 126, 234, 0.1)',
                                      },
                                    }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? 'rgba(255, 255, 255, 0.05)' 
                                  : 'rgba(255, 255, 255, 0.8)',
                                '& fieldset': {
                                  borderColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.1)' 
                                    : 'rgba(102, 126, 234, 0.2)',
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.2)' 
                                    : 'rgba(102, 126, 234, 0.4)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.mode === 'dark' 
                                    ? '#667eea' 
                                    : '#667eea',
                                },
                              },
                              '& .MuiInputBase-input': {
                                color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                '&::placeholder': {
                                  color: theme.palette.mode === 'dark' ? '#b3b3b3' : theme.palette.text.secondary,
                                  opacity: 1,
                                },
                              },
                            }}
                          />
                        </Box>
                        
                        <TableContainer 
                        component={Paper} 
                        sx={{ 
                          mb: 2,
                          borderRadius: 3,
                          overflow: 'hidden',
                          maxHeight: '600px', // Set a max height to enable scrolling
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                            : '0 8px 32px rgba(102, 126, 234, 0.15)',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                          // Mobile-friendly horizontal scrolling
                          overflowX: 'auto',
                          overflowY: 'auto', // Enable vertical scrolling
                          '&::-webkit-scrollbar': {
                            height: 8,
                            width: 8,
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 4,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                            borderRadius: 4,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                            },
                          },
                          // Touch scrolling for mobile
                          WebkitOverflowScrolling: 'touch',
                          scrollBehavior: 'smooth',
                        }}
                      >
                        <Table sx={{ 
                          minWidth: { xs: stage.value === agentNumberCheckboxStage ? 950 : 900, md: 'auto' }, // Ensure minimum width on mobile
                        }}>
                          <TableHead>
                            <TableRow>
                              {stageTableColumns.map((column, index) => (
                                <TableCell 
                                  key={index}
                                  sx={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 15,
                                    backdropFilter: 'blur(8px)',
                                    backgroundColor: theme.palette.mode === 'dark' 
                                      ? 'rgba(42, 42, 42, 0.95)'
                                      : 'rgba(255, 255, 255, 0.95)',
                                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    textAlign: 'center',
                                    py: 2,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                                    // Prevent header text wrapping
                                    whiteSpace: 'nowrap',
                                    minWidth: column === 'Agent Number Sent' ? 150 : index === 0 ? 120 : index === 1 || index === 2 ? 120 : index === 3 ? 150 : 100, // Set minimum widths for key columns
                                  }}
                                >
                                  {column}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stageHires.map((hire, hireIndex) => (
                              <TableRow 
                                key={hire.id}
                                sx={{
                                  '&:nth-of-type(odd)': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.02)'
                                      : 'rgba(102, 126, 234, 0.03)',
                                  },
                                  '&:nth-of-type(even)': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : 'rgba(118, 75, 162, 0.03)',
                                  },
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.08)'
                                      : 'rgba(102, 126, 234, 0.08)',
                                    transform: 'scale(1.01)',
                                    transition: 'all 0.3s ease',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120, textAlign: 'center' }}>
                                  <Tooltip title="Actions">
                                    <IconButton 
                                      onClick={(event) => handleActionsMenuOpen(event, hire)} 
                                      sx={{
                                        color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                                        '&:hover': {
                                          backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(144, 202, 249, 0.1)'
                                            : 'rgba(25, 118, 210, 0.1)',
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.3s ease',
                                      }}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                {/* Notes Column - Replaces old Edit button position */}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100, textAlign: 'center' }}>
                                  <Tooltip title="Notes">
                                    <IconButton 
                                      onClick={() => {
                                        setSelectedHireForNotes(hire);
                                        setNotesDialogOpen(true);
                                      }}
                                      sx={{
                                        color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                        '&:hover': {
                                          backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(102, 126, 234, 0.1)'
                                            : 'rgba(102, 126, 234, 0.1)',
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.3s ease',
                                      }}
                                    >
                                      <NoteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                {/* Agent Number Sent Checkbox - configurable per organization */}
                                {agentNumberCheckboxStage !== null && stage.value === agentNumberCheckboxStage && (
                                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150, textAlign: 'center' }}>
                                    <Checkbox
                                      checked={hire.agent_appointment_sent || false}
                                      onChange={(event) => handleAgentAppointmentSentChange(hire, event.target.checked)}
                                      sx={{
                                        color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                        '&.Mui-checked': {
                                          color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                        },
                                        '&:hover': {
                                          backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(102, 126, 234, 0.1)' 
                                            : 'rgba(102, 126, 234, 0.1)',
                                        },
                                      }}
                                    />
                                  </TableCell>
                                )}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'created_at')}</TableCell>
                                {/* Only show Updated At column for stages other than "Invitation Sent" (stage.value !== 0) */}
                                {stage.value !== 0 && (
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'update_timestamp')}</TableCell>
                                )}
                                {/* Time Since column - uses created_at for stage 0, update_timestamp for others */}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                                  <TimeSinceCell
                                    dateString={stage.value === 0 
                                      ? (hire.created_at as string)
                                      : (hire.update_timestamp as string)
                                    }
                                    label={stage.value === 0 ? 'created' : 'updated'}
                                    displayDate={stage.value === 0 
                                      ? renderEditableCell(hire, 'created_at') as string
                                      : renderEditableCell(hire, 'update_timestamp') as string
                                    }
                                  />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>{renderEditableCell(hire, 'fullName')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'state')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>{renderEditableCell(hire, 'email')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'mobileNumber')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'instagram')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'reference')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'recruiter_numbers')}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'recruiter_emails')}</TableCell>
                                {/* Edit Column - Moved to far right */}
                                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100, textAlign: 'center' }}>
                                  {editingId === hire.id ? (
                                    <>
                                      <Tooltip title="Save">
                                        <IconButton 
                                          onClick={handleSave} 
                                          sx={{
                                            color: '#4caf50',
                                            '&:hover': {
                                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                          }}
                                        >
                                          <SaveIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel">
                                        <IconButton 
                                          onClick={handleCancel} 
                                          sx={{
                                            color: '#f44336',
                                            '&:hover': {
                                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                          }}
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Tooltip title="Edit">
                                      <IconButton 
                                        onClick={() => handleEdit(hire)} 
                                        sx={{
                                          color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                                          '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' 
                                              ? 'rgba(144, 202, 249, 0.1)'
                                              : 'rgba(25, 118, 210, 0.1)',
                                            transform: 'scale(1.1)',
                                          },
                                          transition: 'all 0.3s ease',
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              );
            })}
                    </>
                  )}
                </>
              )}
            </TabPanel>

            <TabPanel value={selectedTab} index={1}>
              <TableContainer component={Paper} elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Team Member Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hire per Code Ratio (Last 2 Months)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hire per Code Ratio (All Time)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Jotform to Hire Ratio (Last 2 Months)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Jotform to Hire Ratio (All Time)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map((member) => {
                      const metric = teamMemberMetrics[member.id];
                      const isLoading = metricsLoading || !metric;
                      
                      return (
                        <TableRow key={member.id} hover>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {member.full_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={member.role}
                              color={getRoleColor(member.role) as any}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {member.team_member_email || member.email || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Tooltip title={`Enrolled in Course: ${metric.currentEnrolled} • Waiting for Training: ${metric.currentWaiting}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {metric.currentRatio === null ? 'N/A' : `${Math.min(metric.currentRatio * 100, 100).toFixed(1)}%`}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Tooltip title={`Enrolled in Course: ${metric.enrolled} • Waiting for Training: ${metric.waiting}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {metric.ratio === null ? 'N/A' : `${Math.min(metric.ratio * 100, 100).toFixed(1)}%`}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Tooltip title={`Jotform Enrolled (Progress 2): ${metric.jotformCurrentEnrolled} • Jotform Invitations (Progress 0-2): ${metric.jotformCurrentInvitations}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {metric.jotformCurrentRatio === null ? 'N/A' : `${Math.min(metric.jotformCurrentRatio * 100, 100).toFixed(1)}%`}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Tooltip title={`Jotform Enrolled (Progress 2): ${metric.jotformEnrolled} • Jotform Invitations (Progress 0-2): ${metric.jotformInvitations}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {metric.jotformRatio === null ? 'N/A' : `${Math.min(metric.jotformRatio * 100, 100).toFixed(1)}%`}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={selectedTab} index={2}>
              <Analytics ownerProfile={ownerProfile} />
            </TabPanel>

            <TabPanel value={selectedTab} index={3}>
              <FrontendSMSManager />
            </TabPanel>

            <TabPanel value={selectedTab} index={4}>
              <EmailReminders />
            </TabPanel>

            <TabPanel value={selectedTab} index={5}>
              <ProLeadGenerator ownerProfile={ownerProfile} />
            </TabPanel>
          </Box>

          <ConfirmArchive
            open={confirmArchiveOpen}
            onClose={handleArchiveCancel}
            onConfirm={handleArchiveConfirm}
            hireName={hireToArchive?.fullName || ''}
          />

          <MassArchiveDialog
            open={massArchiveOpen}
            onClose={() => setMassArchiveOpen(false)}
            onArchiveComplete={fetchAllHires}
            hires={filteredHires}
          />

          <TrademarkFooter />

          <Dialog
            open={assignRecruiterOpen}
            onClose={handleAssignRecruiterCancel}
            aria-labelledby="assign-recruiter-dialog-title"
            aria-describedby="assign-recruiter-dialog-description"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle 
              id="assign-recruiter-dialog-title"
              sx={{
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
              }}
            >
              Assign Recruiter/Owner to {selectedRecruiter?.fullName}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Select admin(s) and/or owner(s) to assign as recruiter(s) for this hire:
              </Typography>
              
              {/* Admins Section */}
              <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                Admins:
              </Typography>
              {adminTeamMembers.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                  No admin team members found.
                </Typography>
              ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper', mb: 3 }}>
                  {adminTeamMembers.map((admin, index) => {
                    const adminEmail = admin.team_member_email || admin.email;
                    const isSelected = selectedAdmins.includes(adminEmail);
                    
                    return (
                      <React.Fragment key={admin.id}>
                        <ListItem 
                          sx={{
                            borderRadius: 2,
                            mb: 1,
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                            backgroundColor: isSelected 
                              ? (theme.palette.mode === 'dark' ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.05)' 
                                : 'rgba(102, 126, 234, 0.05)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleAdminToggle(adminEmail)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                  '&.Mui-checked': {
                                    color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {admin.full_name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {adminEmail}
                                </Typography>
                              </Box>
                            }
                            sx={{ width: '100%', margin: 0 }}
                          />
                        </ListItem>
                        {index < adminTeamMembers.length - 1 && (
                          <Divider sx={{ my: 1 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
              
              {/* Owners Section */}
              <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600, color: theme.palette.success.main }}>
                Owners:
              </Typography>
              {ownerTeamMembers.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                  No owner team members found.
                </Typography>
              ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper', mb: 3 }}>
                  {ownerTeamMembers.map((owner, index) => {
                    const ownerEmail = owner.team_member_email || owner.email;
                    const isSelected = selectedOwners.includes(ownerEmail);
                    
                    return (
                      <React.Fragment key={owner.id}>
                        <ListItem 
                          sx={{
                            borderRadius: 2,
                            mb: 1,
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                            backgroundColor: isSelected 
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.05)' 
                                : 'rgba(76, 175, 80, 0.05)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleOwnerToggle(ownerEmail)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? '#4caf50' : '#4caf50',
                                  '&.Mui-checked': {
                                    color: theme.palette.mode === 'dark' ? '#4caf50' : '#4caf50',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {owner.full_name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {ownerEmail}
                                </Typography>
                              </Box>
                            }
                            sx={{ width: '100%', margin: 0 }}
                          />
                        </ListItem>
                        {index < ownerTeamMembers.length - 1 && (
                          <Divider sx={{ my: 1 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
              
              {(selectedAdmins.length > 0 || selectedOwners.length > 0) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    Selected Recruiter(s): {[...selectedAdmins, ...selectedOwners].join(', ')}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
              <Button 
                onClick={handleAssignRecruiterCancel}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                  color: theme.palette.mode === 'dark' ? '#667eea' : '#667eea',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#764ba2' : '#764ba2',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignRecruiterConfirm}
                variant="contained"
                disabled={selectedAdmins.length === 0 && selectedOwners.length === 0}
                sx={{
                  background: (selectedAdmins.length === 0 && selectedOwners.length === 0)
                    ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: (selectedAdmins.length === 0 && selectedOwners.length === 0) ? 'text.disabled' : 'white',
                  fontWeight: 600,
                  '&:hover': {
                    background: (selectedAdmins.length === 0 && selectedOwners.length === 0)
                      ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                      : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                Assign Recruiter{selectedAdmins.length + selectedOwners.length > 1 ? 's' : ''}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Move Hire Dialog */}
          {selectedHireForMove && (
            <MoveHireDialog
              open={moveHireOpen}
              onClose={() => {
                setMoveHireOpen(false);
                setSelectedHireForMove(null);
              }}
              hire={selectedHireForMove}
              onMoveSuccess={handleMoveSuccess}
            />
          )}

          {/* Actions Menu */}
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={handleActionsMenuClose}
            PaperProps={{
              sx: {
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                  : '0 8px 32px rgba(102, 126, 234, 0.15)',
                borderRadius: 2,
                minWidth: 180,
              }
            }}
          >
            <MenuItem 
              onClick={handleAssignFromMenu}
              sx={{
                color: theme.palette.mode === 'dark' ? '#4caf50' : '#4caf50',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(76, 175, 80, 0.1)' 
                    : 'rgba(76, 175, 80, 0.1)',
                },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AssignmentIcon fontSize="small" />
              Assign Recruiter
            </MenuItem>
            <MenuItem 
              onClick={handleArchiveFromMenu}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <ArchiveIcon fontSize="small" />
              Archive
            </MenuItem>
            <MenuItem 
              onClick={handleMoveFromMenu}
              sx={{
                color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.1)' 
                    : 'rgba(25, 118, 210, 0.1)',
                },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <TrendingUpIcon fontSize="small" />
              Move Hire
            </MenuItem>
          </Menu>

      {/* Notes Dialog */}
      <NotesDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setSelectedHireForNotes(null);
        }}
        hire={selectedHireForNotes}
        onSave={(updatedNotes) => {
          // Update the local state without refreshing the entire dashboard
          if (selectedHireForNotes) {
            setHires(prevHires => 
              prevHires.map(hire => 
                hire.id === selectedHireForNotes.id 
                  ? { ...hire, notes: updatedNotes }
                  : hire
              )
            );
            setFilteredHires(prevFilteredHires => 
              prevFilteredHires.map(hire => 
                hire.id === selectedHireForNotes.id 
                  ? { ...hire, notes: updatedNotes }
                  : hire
              )
            );
          }
        }}
      />
        </Container>
      </Box>
    </Box>
  );
};

export default OwnerDashboard; 